import { useEffect, useState, useContext } from "react";
import { StyleSheet, View, Text } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

import { appColors } from "../constants/colors";
import InputsOuterContainer from "./UI/InputsOuterContainer";
import calculate from "../utils/calculate";
import { sanitizeNumericInput } from "../utils/helpers";
import Input from "./Input";
import { CalculationsContext } from "../store/calculationsCtx";

// Field must live outside InputsContainer so it is never recreated on re-render,
// which would unmount the focused TextInput and dismiss the keyboard.
const Field = ({ label, desc, isError, value, onFocus, onBlur, onChangeText, errorStyle }) => (
  <View style={styles.row}>
    <Text style={styles.label}>{label}</Text>
    <Input
      isError={isError}
      error={errorStyle}
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
      onChangeText={onChangeText}
    />
  </View>
);

export default function InputsContainer({ category, currency }) {
  const [loanError, setLoanError] = useState(false);
  const [termError, setTermError] = useState(false);
  const [rateError, setRateError] = useState(false);
  const [inputKey, setInputKey] = useState(1);

  const calculationsCtx = useContext(CalculationsContext);
  const inputs = calculationsCtx.inputs;

  useEffect(() => {
    const handleInputsChanged = async () => {
      if (inputKey > 1) {
        const calculators = calculationsCtx.calculators;
        const inputs = calculationsCtx.inputs;

        const newArray = calculators.map(item => {
          if (item.hasOwnProperty("inputs") && item.active) {
            return { ...item, inputs };
          }
          return item;
        });

        await AsyncStorage.setItem("calculators", JSON.stringify(newArray));
        calculationsCtx.setCalculators(newArray);
      }
    };

    handleInputsChanged();
  }, [inputKey]);

  useEffect(() => {
    const inputValues = Object.values(inputs);

    if (inputValues.includes("")) return;

    const numberInputValues = inputValues.map(e => parseFloat(e) || 0);

    if (numberInputValues[0] === 0) {
      setLoanError(true);
      return;
    }
    if (numberInputValues[4] === 0) {
      setTermError(true);
      return;
    }
    if (numberInputValues[5] === 0) {
      setRateError(true);
      return;
    }

    const results1 = calculate(numberInputValues, currency);
    const { instalment, loan, fees, interestPaid, totalPaid, interest, totalLoan, balloon, msg } = results1;

    calculationsCtx.setResults({ instalment, loan, fees, interestPaid, totalPaid, interest, totalLoan, balloon, msg });
    calculationsCtx.setTableResults(results1.unmodResults);
  }, [category, currency, inputs, inputs.loanAmount]);

  function inputUpdateHandler(desc, value0) {
    setInputKey(key => key + 1);
    const value = sanitizeNumericInput(value0);

    if (desc === "loanAmount" && loanError) setLoanError(false);
    if (desc === "term" && termError) setTermError(false);
    if (desc === "rate" && rateError) setRateError(false);

    calculationsCtx.setInputs(prevState => ({ ...prevState, [desc]: value }));
  }

  function emptyInput(desc) {
    if (+inputs[desc] !== 0) return;
    inputUpdateHandler(desc, "0");
  }

  function fillInput(desc) {
    if (inputs[desc] === "") inputUpdateHandler(desc, "0");
  }

  const fieldProps = desc => ({
    value: inputs[desc],
    onFocus: () => emptyInput(desc),
    onBlur: () => fillInput(desc),
    onChangeText: val => inputUpdateHandler(desc, val),
    errorStyle: styles.error,
  });

  return (
    <InputsOuterContainer>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Loan inputs</Text>
      </View>
      <View style={styles.inputs}>
        <Field
          label={category !== "Personal Loan" ? "Purchase amount" : "Loan amount"}
          desc="loanAmount"
          isError={loanError}
          {...fieldProps("loanAmount")}
        />

        {category === "Car Finance" && <Field label="Optional extras" desc="options" {...fieldProps("options")} />}

        {category !== "Personal Loan" && <Field label="Deposit" desc="deposit" {...fieldProps("deposit")} />}

        {category === "Car Finance" && (
          <Field label="Trade-in value" desc="tradeInValue" {...fieldProps("tradeInValue")} />
        )}

        <Field label="Term (months)" desc="term" isError={termError} {...fieldProps("term")} />
        <Field label="Interest rate (%)" desc="rate" isError={rateError} {...fieldProps("rate")} />

        {category === "Car Finance" && <Field label="Balloon (%)" desc="balloon" {...fieldProps("balloon")} />}

        <Field label="Initiation fee" desc="initFee" {...fieldProps("initFee")} />
        <Field label="Monthly service fee" desc="serviceFee" {...fieldProps("serviceFee")} />
      </View>
    </InputsOuterContainer>
  );
}

const styles = StyleSheet.create({
  sectionHeader: {
    width: "100%",
    marginBottom: 8,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: appColors.textPrimary,
  },

  inputs: {
    width: "100%",
    gap: 2,
  },

  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 7,
    borderBottomWidth: 0.5,
    borderBottomColor: appColors.borderLight,
  },

  label: {
    fontSize: 12,
    color: appColors.textSecondary,
    flex: 1,
  },

  error: {
    backgroundColor: appColors.backgroundError,
    borderColor: appColors.error,
    borderWidth: 1,
  },
});
